import { Header, TableCard } from "@general-backoffice/core";
import { StatsCard } from "@general-backoffice/core/index";
import { type BotConfig, UserConfigSchema } from "@investhinks/api-client";
import moment from "moment";
import React, { type ChangeEvent, useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { useSearchParams } from "react-router-dom";
import {
  Badge,
  Button,
  Card,
  Col,
  Container,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
} from "reactstrap";

import { adminStatsEndpoints, botConfigsEndpoints } from "../../../../api";
import useBots from "../../../../components/bots/hooks/useBots";
import FilterCheckbox from "../../../../components/shared/FilterCheckbox";
import NumberStatsCard from "../../../../components/shared/NumberStatsCard";
import PaginatedTableFooter from "../../../../components/shared/PaginatedTableFooter";
import type { SortDirection } from "../../../../hooks/strapi/useStrapiSort";
import getCurrencySymbol from "../../../../utils/getCurrencySymbol";
import isVipOrZeroPriceTier from "../../../../utils/isVipOrZeroPriceTier";
import { getStrategyName } from "../../../../utils/risks";
import { TypedTable } from "../../../../utils/typedLib";

// Helper to safely parse userConfig from Record<string, unknown> to typed UserConfig
const parseUserConfig = (userConfig: Record<string, unknown> | null | undefined) => {
  if (!userConfig) return undefined;
  const result = UserConfigSchema.safeParse(userConfig);
  return result.success ? result.data : undefined;
};

interface BotsProps {
  filtersToShow?: string[];
}

const riskColors: Record<string, string> = {
  high: "danger",
  medium: "warning",
  low: "success",
};

const STALE_SNAPSHOT_HOURS = 2;
const MONTHLY_STATS_FIRST_PERIOD = "2024-09";

const Bots: React.FC<BotsProps> = ({ filtersToShow = [] }) => {
  const { t } = useTranslation("dashboard", { keyPrefix: "adminPages.bots" });
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Deep links from the admin overview ("Needs attention") arrive as
  // ?filter=onLoss / ?filter=stale / ?filter=nearStopLoss&ids=1,2.
  const deepLinkFilter = searchParams.get("filter");

  // Single request for active bots - used for both cards and table
  // Only populate fields actually used by the table
  const { bots, isLoading, pagination, goToPage, changePageSize, changeSort, sort, filters, changeFilters } = useBots({
    sort: {},
    populate: ["strategy", "stats", "exchange_account", "user"],
    pagination: { pageSize: 10 },
    filters: {
      disabled: { $ne: true },
      user: { isTest: { $ne: true } },
      strategy: { $notNull: true }, // Filter out Anton's tests
      id: { $ne: 49 }, // Filter out a specific Anton test
      exchange_account: { status: { $ne: "INVALID" } }, // Filter out invalid accounts
      // Include exchange accounts where hidden is false OR null
      $or: [{ exchange_account: { hidden: false } }, { exchange_account: { hidden: { $null: true } } }],
    },
  });

  // Applied as a user modification rather than as an initial filter: this
  // hook family discards every manual filter whenever the initial filters
  // change, so a deep link in there meant unticking one checkbox also wiped
  // the search box and any other active filter. The URL param is left in
  // place, so a reload reapplies the filter.
  useEffect(() => {
    if (deepLinkFilter === "onLoss") changeFilters("onLoss", true);
    else if (deepLinkFilter === "stale") changeFilters("staleSnapshotHours", STALE_SNAPSHOT_HOURS);
    else if (deepLinkFilter === "nearStopLoss") {
      // The overview computes equity-vs-stop-loss itself (Strapi filters cannot
      // compare two fields) and links the exact bots it counted.
      const ids = (searchParams.get("ids") ?? "").split(",").map(Number).filter((id) => Number.isInteger(id) && id > 0);
      changeFilters("id", { $in: ids.length ? ids : [0] });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deepLinkFilter]);

  // Fetch all active bots for balance aggregation cards (paginated to respect API limit)
  const [allBots, setAllBots] = useState<BotConfig[]>([]);
  const [isLoadingAll, setIsLoadingAll] = useState(true);
  useEffect(() => {
    let cancelled = false;
    const balanceFilters = {
      disabled: { $ne: true as const },
      user: { isTest: { $ne: true as const } },
      strategy: { $notNull: true as const },
      id: { $ne: 49 },
      exchange_account: { status: { $ne: "INVALID" as const } },
      $or: [{ exchange_account: { hidden: false } }, { exchange_account: { hidden: { $null: true } } }],
    };
    const fetchAll = async () => {
      setIsLoadingAll(true);
      const all: BotConfig[] = [];
      let page = 1;
      try {
        for (;;) {
          const res = await botConfigsEndpoints.getBotConfigs({
            populate: ["stats", "user", "user.subscription_tier"],
            pagination: { pageSize: 100, page },
            filters: balanceFilters,
          });
          if (cancelled) return;
          all.push(...(res.data || []));
          const total = res.meta?.pagination?.total ?? 0;
          if (all.length >= total) break;
          page++;
        }
        if (!cancelled) setAllBots(all);
      } catch (e) {
        console.error("Failed to fetch bots for balance cards", e);
      } finally {
        if (!cancelled) setIsLoadingAll(false);
      }
    };
    fetchAll();
    return () => { cancelled = true; };
  }, []);

  const balanceCards = useMemo(() => {
    const paid: Record<string, number> = {};
    const free: Record<string, number> = {};

    for (const bot of allBots) {
      const balance = bot.stats?.balance;
      if (balance == null) continue;
      const currency = bot.stats?.baseCurrency || "EUR";
      const bucket = isVipOrZeroPriceTier(bot.user) ? free : paid;
      bucket[currency] = (bucket[currency] || 0) + balance;
    }

    const formatBucket = (bucket: Record<string, number>) => {
      const eur = bucket["EUR"] || 0;
      const others = Object.entries(bucket)
        .filter(([c]) => c !== "EUR")
        .sort(([, a], [, b]) => b - a);
      return { eur, others };
    };

    return { paid: formatBucket(paid), free: formatBucket(free) };
  }, [allBots]);

  const [isLoadingFullStats, setIsLoadingFullStats] = useState<boolean>(false);
  const [fullStatsError, setFullStatsError] = useState<string | null>(null);
  const [isFullStatsModalOpen, setIsFullStatsModalOpen] = useState(false);
  const [fullStatsPeriod, setFullStatsPeriod] = useState(() => moment().format("YYYY-MM"));
  const currentStatsPeriod = moment().format("YYYY-MM");

  const downloadFullStatsCSV = useCallback(async (period: string) => {
    setIsLoadingFullStats(true);
    setFullStatsError(null);
    try {
      const { data } = await adminStatsEndpoints.getMonthlyFullStatsPerBot({ period });
      const blob = new Blob([data], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `fullBotStats_${period}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      setIsFullStatsModalOpen(false);
    } catch (err: unknown) {
      console.error(err);
      setFullStatsError(t("report.error"));
    } finally {
      setIsLoadingFullStats(false);
    }
  }, [t]);

  const columns = [
    {
      name: "User",
      formattedContent: ({ user }: BotConfig) => (
        <>
          <div>{user?.name}</div>
          <small className="font-weight-bold">{user?.email}</small>
        </>
      ),
      isTh: true,
      sort: (d: SortDirection) => changeSort("user.name", d),
      sortStatus: sort?.["user.name"],
    },
    {
      name: "Name",
      formattedContent: ({ exchange_account }: BotConfig) => {
        return exchange_account?.name ?? "-------------";
      },
      isTh: true,
      sort: (d: SortDirection) => changeSort("exchange_account.name", d),
      sortStatus: sort?.["exchange_account.name"],
    },
    {
      name: "Strategy",
      isTh: true,
      formattedContent: ({ userConfig, strategy }: BotConfig) => {
        const config = parseUserConfig(userConfig);
        return getStrategyName(strategy, config?.risk);
      },
    },
    {
      name: "Risk",
      formattedContent: ({ userConfig }: BotConfig) => {
        const config = parseUserConfig(userConfig);
        return (
          <Badge color={riskColors?.[config?.risk || "low"]} className="text-uppercase p-2 text-default">
            {config?.risk}
          </Badge>
        );
      },
    },
    {
      name: "Profit",
      formattedContent: ({ stats }: BotConfig) => {
        if (!stats) return "----.-- - (--.-- %)";
        const { profit_percentage, baseCurrency, absolute_profit } = stats || {};
        return (
          <div className={(absolute_profit || 0) < 0 ? "text-danger" : undefined}>
            {parseFloat(String(absolute_profit || 0)).toFixed(2)} {getCurrencySymbol(baseCurrency)}{" "}
            <b>({profit_percentage || 0} %)</b>
          </div>
        );
      },
      sort: (d: SortDirection) => changeSort("stats.absolute_profit", d),
      sortStatus: sort?.["stats.absolute_profit"],
    },
    {
      name: "Drawdown",
      formattedContent: ({ stats }: BotConfig) => {
        const { drawdown } = stats || {};
        return `${drawdown ?? "--.--"} %`;
      },
      sort: (d: SortDirection) => changeSort("stats.drawdown", d),
      sortStatus: sort?.["stats.drawdown"],
    },
    {
      name: "Expected profit",
      formattedContent: ({ stats }: BotConfig) => {
        if (!stats) return "----.-- -";
        const { expected_profit, baseCurrency } = stats || {};
        return (
          <div>
            {parseFloat(String(expected_profit || 0)).toFixed(2)} {getCurrencySymbol(baseCurrency)}
          </div>
        );
      },
      sort: (d: SortDirection) => changeSort("stats.expected_profit", d),
      sortStatus: sort?.["stats.expected_profit"],
    },
    {
      name: "Balance",
      formattedContent: ({ stats }: BotConfig) => {
        if (!stats) return "----.-- -";
        const { balance, baseCurrency } = stats || {};
        return `${parseFloat(String(balance || 0)).toFixed(2)} ${getCurrencySymbol(baseCurrency)}`;
      },
      sort: (d: SortDirection) => changeSort("stats.balance", d),
      sortStatus: sort?.["stats.balance"],
    },
    {
      name: "Equity",
      formattedContent: ({ stats }: BotConfig) => {
        if (!stats) return "----.-- -";
        const { equity, baseCurrency } = stats || {};
        return `${parseFloat(String(equity || 0)).toFixed(2)} ${getCurrencySymbol(baseCurrency)}`;
      },
      sort: (d: SortDirection) => changeSort("stats.equity", d),
      sortStatus: sort?.["stats.equity"],
    },
    {
      name: "Leverage",
      formattedContent: ({ stats }: BotConfig) => {
        const { leverage } = stats || {};
        if (!stats) return "----";
        return leverage;
      },
      sort: (d: SortDirection) => changeSort("stats.leverage", d),
      sortStatus: sort?.["stats.leverage"],
    },
    {
      name: "Trades",
      formattedContent: ({ stats }: BotConfig) => {
        const { openTrades, trades } = stats || {};
        return `${openTrades || "---"} / ${trades || "---"}`;
      },
      sort: (d: SortDirection) => changeSort("stats.openTrades", d),
      sortStatus: sort?.["stats.openTrades"],
    },
    {
      name: "Stoploss",
      formattedContent: ({ stats }: BotConfig) => {
        const { stopLoss, baseCurrency } = stats || {};
        if (!stats || stopLoss === 0) return "----.-- -";
        return `${parseFloat(String(stopLoss || 0)).toFixed(2)} ${getCurrencySymbol(baseCurrency)}`;
      },
      sort: (d: SortDirection) => changeSort("stats.stopLoss", d),
      sortStatus: sort?.["stats.stopLoss"],
    },
    {
      name: "Sailing mode",
      formattedContent: ({ userConfig, stats }: BotConfig) => {
        const config = parseUserConfig(userConfig);
        const { sailingModeThreshold } = config || {};
        if (!sailingModeThreshold) return "--";
        const { drawdown } = stats || {};
        const active = parseFloat(String(drawdown)) > parseInt(String(sailingModeThreshold));
        return (
          <div className={active ? "text-success font-weight-bold" : "text-muted"}>
            {sailingModeThreshold} % <i className="fas fa-power-off" />
          </div>
        );
      },
      // sort: d => changeSort("stats.stopLoss", d),
      // sortStatus: sort?.["stats.stopLoss"]
    },
    {
      name: "Last update",
      formattedContent: ({ stats }: BotConfig) => stats?.timestamp && moment(stats?.timestamp).fromNow(),
      sort: (d: SortDirection) => changeSort("stats.timestamp", d),
      sortStatus: sort?.["stats.timestamp"],
    },
  ];

  return (
    <React.Fragment>
      <Header.BreadCrumb color="secondary" isDark={false}>
        <div className="position-absolute mt-3 top-3 right-4">
          <Button
            onClick={() => {
              setFullStatsError(null);
              setIsFullStatsModalOpen(true);
            }}
            size="sm"
            color="default"
            style={{ minWidth: 150 }}
          >
            <><i className="fas fa-download mr-2" /> {t("report.button")}</>
          </Button>
        </div>

        <Col xs={12} md={6} xl={3} className="d-flex">
          <StatsCard.Number
            color="default"
            isDark={true}
            number={pagination?.total}
            suffix={"Bots"}
            title={"Active bots"}
            icon="fas fa-robot"
            description={"Total number of active bots"}
          />
        </Col>
        <Col xs={12} md={6} xl={4} className="d-flex">
          <NumberStatsCard
            color="success"
            isDark={true}
            title="Paid users balance"
            icon="fas fa-coins"
            number={balanceCards.paid.eur}
            prefix="€"
            decimals={0}
            isLoading={isLoadingAll}
            description={
              balanceCards.paid.others.length > 0 ? (
                <>
                  {balanceCards.paid.others.map(([currency, amount]) => (
                    <span key={currency} className="mr-2">
                      {getCurrencySymbol(currency)}{amount.toLocaleString(undefined, { maximumFractionDigits: 0 })} {currency}
                    </span>
                  ))}
                </>
              ) : undefined
            }
          />
        </Col>
        <Col xs={12} md={6} xl={4} className="d-flex">
          <NumberStatsCard
            color="info"
            isDark={true}
            title="Free / VIP balance"
            icon="fas fa-gift"
            number={balanceCards.free.eur}
            prefix="€"
            decimals={0}
            isLoading={isLoadingAll}
            description={
              balanceCards.free.others.length > 0 ? (
                <>
                  {balanceCards.free.others.map(([currency, amount]) => (
                    <span key={currency} className="mr-2">
                      {getCurrencySymbol(currency)}{amount.toLocaleString(undefined, { maximumFractionDigits: 0 })} {currency}
                    </span>
                  ))}
                </>
              ) : undefined
            }
          />
        </Col>
      </Header.BreadCrumb>
      <Container className="mt--6" fluid>
        <Row>
          <Col>
            <Card className="overflow-hidden">
              <TableCard.Header
                title={"Active Bots"}
                isLoading={isLoading}
                search={{
                  value: filters?.["search"],
                  onChange: (e: ChangeEvent<HTMLInputElement>) => changeFilters("search", e.target.value),
                  msToChange: 1000,
                  placeholder: t("searchPlaceholder"),
                }}
              >
                {!!filtersToShow.length && (
                  <div className="px-4 flex-center justify-content-end flex-wrap" style={{ gap: ".8rem 1rem" }}>
                    {filtersToShow.includes("showOnlyOnLoss") && (
                      <FilterCheckbox
                        label={t("filters.onLoss")}
                        checked={!!filters?.["onLoss"]}
                        onChange={(checked) => changeFilters("onLoss", checked ? true : null)}
                        disabled={isLoading}
                      />
                    )}
                    {filtersToShow.includes("showOnlyStale") && (
                      <FilterCheckbox
                        label={t("filters.stale", { hours: STALE_SNAPSHOT_HOURS })}
                        checked={!!filters?.["staleSnapshotHours"]}
                        onChange={(checked) =>
                          changeFilters("staleSnapshotHours", checked ? STALE_SNAPSHOT_HOURS : null)
                        }
                        disabled={isLoading}
                      />
                    )}
                  </div>
                )}
              </TableCard.Header>
              <TypedTable
                columns={columns}
                rows={bots}
                isLoading={isLoading}
                onRowClick={(row) => {
                  navigate(`/users/${row.user?.id}/exchange-account/${row.exchange_account?.id}`);
                }}
                noContentMessage="No bots were found"
                rowAmount={pagination?.pageSize}
                selectable={false}
                sortable={false}
              />
              <PaginatedTableFooter
                pagination={pagination}
                isLoading={isLoading}
                goToPage={goToPage}
                changePageSize={changePageSize}
                amountOptions={[10, 25, 50, 100]}
              />
            </Card>
          </Col>
        </Row>
      </Container>

      <Modal
        isOpen={isFullStatsModalOpen}
        toggle={() => !isLoadingFullStats && setIsFullStatsModalOpen(false)}
        centered
        size="md"
      >
        <ModalHeader toggle={() => !isLoadingFullStats && setIsFullStatsModalOpen(false)}>
          <div className="d-flex align-items-center">
            <i className="fas fa-file-download text-primary mr-2" />
            {t("report.title")}
          </div>
        </ModalHeader>
        <ModalBody>
          <p className="text-muted mb-4">{t("report.description")}</p>
          <Label for="monthly-stats-period" className="font-weight-bold">
            {t("report.periodLabel")}
          </Label>
          <Input
            id="monthly-stats-period"
            type="month"
            value={fullStatsPeriod}
            min={MONTHLY_STATS_FIRST_PERIOD}
            max={currentStatsPeriod}
            onChange={(event) => {
              setFullStatsPeriod(event.target.value);
              setFullStatsError(null);
            }}
            disabled={isLoadingFullStats}
            aria-describedby="monthly-stats-period-help"
          />
          <small id="monthly-stats-period-help" className="form-text text-muted">
            {t("report.periodHelp")}
          </small>
          {fullStatsError && (
            <div role="alert" className="alert alert-danger mt-3 mb-0">
              <i className="fas fa-exclamation-circle mr-2" />
              {fullStatsError}
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button
            color="secondary"
            outline
            onClick={() => setIsFullStatsModalOpen(false)}
            disabled={isLoadingFullStats}
          >
            {t("report.cancel")}
          </Button>
          <Button
            color="primary"
            onClick={() => downloadFullStatsCSV(fullStatsPeriod)}
            disabled={!fullStatsPeriod || isLoadingFullStats}
          >
            {isLoadingFullStats ? (
              <><i className="fas fa-spinner fa-spin mr-2" /> {t("report.generating")}</>
            ) : (
              <><i className="fas fa-download mr-2" /> {t("report.generate")}</>
            )}
          </Button>
        </ModalFooter>
      </Modal>
    </React.Fragment>
  );
};

export default Bots;
